import React from 'react';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { DocAccessType, IRisk, Scholarship, ScholarshipDocList, ScholarshipSponsorship } from '../../../../types';
import ScholarshipLetter from '../../../sections/financeDocs/scholarshipLetter';
import { formatCurrency } from '../../../../utilities/formatting';
import { isFundingRisk } from '../../../../utilities/risks';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { sponsorFundLabel } from '../../../../utilities/sponsors';
import { UserRole } from '../../../../store/auth/types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryGroup } from '../../../Summary/SummaryGroup';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  documents: Scholarship;
  financials: ScholarshipSponsorship;
  risk: IRisk['fundingRisk']['scholarshipSponsorship'];
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;

  isCheckedOffDocRisks?: (docType: string, docKey: string) => boolean;
  onCheckOffDocRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const hasEmailOrOfferLetter = (documentationType: string) =>
  ['Email from university', 'In my offer letter'].includes(documentationType || '');

const ScholarshipSponsorshipSection = (props: IProps) => {
    const { t } = useTranslation('common');
  const { showRisks, infoOnly, userAccess, userRole } = props;

  const sponsorAmount = sponsorFundLabel(props.financials);

  const getMetaData = () => {
    return (
      <SummaryGroup
        title={props.financials.scholarshipName}
        key={`scholarship_sponsor_${props.id}`}
        boxProps={{ as: 'div', pt: 0 }}
      >
        <SummaryRow
          key={`scholarship_sponsor_name_${props.id}`}
          label={t('common:name')}
          fullLabel="Scholarship Name"
          value={props.financials.scholarshipName!}
          isRisk={showRisks && isFundingRisk(props.risk, 'scholarshipName', props.id)}
        />

        <SummaryRow
          key={`scholarship_nature_type_${props.id}`}
          label={t('common:scholarshipTowards')}
          fullLabel="Select the costs that your scholarship can be used towards:"
          value={props.financials.sponsorshipCovers.join(', ')}
          isRisk={showRisks && false}
        />

        {props.financials.courseAndLivingFundSeparate !== undefined ? (
          <SummaryRow
            key={`scholarship_living_costs_separate_${props.id}`}
            label={t('common:separateLivingCosts')}
            fullLabel="Are your living costs listed separately from your tuition in your scholarship documentation?"
            value={props.financials.courseAndLivingFundSeparate ? 'Yes' : 'No'}
            isRisk={showRisks && false}
          />
        ) : null}

        {props.financials.courseAndLivingFundSeparate ? (
          <>
            <SummaryRow
              key={`scholarship_living_costs_separate_${props.id}`}
              label={t('common:livingCostsContribution')}
              fullLabel="How much of your living costs does this scholarship cover?"
              value={props.financials.livingFundType}
              isRisk={showRisks && false}
            />

            {props.financials.livingFundType === 'Percentage' ? (
              <SummaryRow
                key={`scholarship_living_fund_percentage_${props.id}`}
                label={t('common:percentageCovered')}
                fullLabel="What percentage is being covered?"
                value={props.financials.livingFundPercentage}
                isRisk={showRisks && false}
              />
            ) : null}

            {props.financials.livingFundType === 'Amount' ? (
              <SummaryRow
                key={`scholarship_living_fund_amount_${props.id}`}
                label={t('common:stipendUpfrontPayment')}
                fullLabel="Will your living costs be provided to you as a monthly stipend / payment or provided to you as an upfront payment?"
                value={props.financials.livingFundAmountType}
                isRisk={showRisks && false}
              />
            ) : null}

            {props.financials.livingFundAmountType === 'Monthly' ||
            props.financials.livingFundAmountType === 'Total' ? (
              <SummaryRow
                key={`scholarship_living_fund_monthly_${props.id}`}
                label={t('common:amount', { livingFundAmountType: props.financials.livingFundAmountType })}
                fullLabel={`What is the ${props.financials.livingFundAmountType.toLowerCase()} amount for living costs?`}
                value={`£${formatCurrency(props.financials.livingFundAmount)}`}
                isRisk={showRisks && false}
              />
            ) : null}

            <SummaryRow
              key={`scholarship_tuition_contribution_type_${props.id}`}
              label={t('common:tuitionFeesContribution')}
              fullLabel="How much of the tuition fees does the scholarship cover?"
              value={props.financials.courseFundType}
              isRisk={showRisks && false}
            />

            {props.financials.courseFundType === 'Percentage' ? (
              <SummaryRow
                key={`scholarship_tuition_contribution_percentage_${props.id}`}
                label={t('common:percentageCovered')}
                fullLabel="What percentage is being covered?"
                value={`${props.financials.courseFundPercentage}%`}
                isRisk={showRisks && false}
              />
            ) : null}

            {props.financials.courseFundType === 'Amount' ? (
              <SummaryRow
                key={`scholarship_tuition_contribution_amount_${props.id}`}
                label={t('common:courseContribution')}
                fullLabel="What is the amount being covered in pounds?"
                value={`£${formatCurrency(props.financials.courseFundAmount)}`}
                isRisk={showRisks && false}
              />
            ) : null}
          </>
        ) : (
          <>
            <SummaryRow
              key={`scholarship_tuition_and_living_cost_contribution_${props.id}`}
              label={t('common:tuitionAndLivingCostsContributio')}
              fullLabel="How much of your tuition fees and living costs does this scholarship cover?"
              value={props.financials.courseAndLivingFundType}
              isRisk={showRisks && false}
            />

            {props.financials.courseAndLivingFundType === 'Percentage' ? (
              <SummaryRow
                key={`scholarship_tuition_and_living_cost_percentage_${props.id}`}
                label={t('common:percentageCovered')}
                fullLabel="What percentage is being covered?"
                value={`${props.financials.courseAndLivingFundPercentage}%`}
                isRisk={showRisks && false}
              />
            ) : null}

            {props.financials.courseAndLivingFundType === 'Amount' ? (
              <SummaryRow
                key={`scholarship_tuition_and_living_cost_percentage_${props.id}`}
                label={t('common:amountCovered')}
                fullLabel="What is the amount being covered in pounds?"
                value={`£${formatCurrency(props.financials.courseAndLivingFundAmount)}`}
                isRisk={showRisks && false}
              />
            ) : null}
          </>
        )}
      </SummaryGroup>
    );
  };

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];
  const scholarDocs = props.documents;

  if (scholarDocs) {
    const visibleDocs = getVisible({
      documentsHash: scholarDocs,
      userAccess,
      infoOnly,
      userRole,
    }) as ScholarshipDocList[];
    if (hasDeleted(scholarDocs.documents)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: ScholarshipDocList, index: number) => {
          const { key, item } = doc;
          const hasEmailOrLetter = hasEmailOrOfferLetter(item.documentationType || '');
          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`${key}-scholarship-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text9', { scholarshipName: props.financials.scholarshipName, index: Object.keys(scholarDocs).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks
                  ? props.isCheckedOffDocRisks('scholarshipSponsorship', props.id + key)
                  : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks(
                  'scholarshipSponsorship',
                  props.id + key,
                  `${props.financials.scholarshipName} (Scholarship Sponsorship)`,
                )
              }
              onUploadClick={() => props.onUploadClick(key)}
              onOpenClick={() => props.onOpenClick(key)}
              onApproveClick={() => props.onApproveClick(key)}
              onRejectClick={() => props.onRejectClick(key)}
              onRescindRejectClick={() => props.onRescindRejectClick(key)}
              onDeleteClick={() => props.onDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {(hasEmailOrLetter || (item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0)) && (
                <ScholarshipLetter documentationType={item.documentationType || ''} />
              )}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={props.id}
      label={props.financials.scholarshipName}
      amount={sponsorAmount}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      metaData={getMetaData()}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default ScholarshipSponsorshipSection;
